import type { AxiosError } from 'axios';
import client from './client';

/** Runner types a skill can never run on. */
export type RunnerType = 'cloud' | 'local';

export interface AgentSkill {
  id: string | null;
  name: string;
  description: string;
  /** For built-in skills, only populated when the caller is an Aibuild admin. */
  instructions: string | null;
  tools: string[];
  builtIn: boolean;
  updatedAt: string | null;
  /** Entitlement gate needed to load the skill; null = available to everyone. */
  requiredGate?: string | null;
  /**
   * True when the whole organization can load the skill. A custom skill that is
   * not shared is individual to the signed-in user. Built-ins are always shared.
   */
  shared: boolean;
  /**
   * Runner types this skill cannot run on; absent or empty = runs anywhere.
   * Always empty for custom skills.
   */
  hiddenOn?: RunnerType[];
  /**
   * The signed-in user's own switch. False means they turned the skill off, so
   * their agent is never offered it and process runs they start refuse to use
   * it. It says nothing about their colleagues.
   */
  enabled: boolean;
  /**
   * Whether the signed-in user may edit or delete the skill: their own
   * individual skill, a shared skill they authored, or any shared skill when
   * they are an org admin. Always false for built-ins.
   */
  manageable: boolean;
}

export interface AgentSkillsListResponse {
  skills: AgentSkill[];
  /**
   * Switches left pointing at a skill that no longer exists, as skill keys.
   * Clearing one is `setAgentSkillEnabled(key, true)`.
   */
  staleDisabledSkillIds: string[];
}

/**
 * How the API names a skill in a preference: built-ins by name, custom skills
 * by row id, so renaming one keeps the preference attached.
 */
export function skillKey(skill: AgentSkill): string {
  return skill.builtIn ? skill.name : `custom:${skill.id}`;
}

export interface AgentSkillRequest {
  name: string;
  description: string;
  instructions: string;
  /** Skill-gated runner tools this skill unlocks; empty for a prose-only skill. */
  tools: string[];
  /**
   * Share with the whole organization. Omitted or false creates an individual
   * skill. On update it moves an existing skill between the two scopes, and
   * un-sharing hands the skill to the acting user.
   */
  shared?: boolean;
}

export async function listAgentSkills(): Promise<AgentSkillsListResponse> {
  const response = await client.get<AgentSkillsListResponse>('/api/v1/agent-skills');
  return response.data;
}

export async function createAgentSkill(request: AgentSkillRequest): Promise<AgentSkill> {
  const response = await client.post<AgentSkill>('/api/v1/agent-skills', request);
  return response.data;
}

export type SkillUsageKind = 'APP' | 'PROCESS';

export interface SkillUsage {
  kind: SkillUsageKind;
  name: string;
}

/** Why an un-share or delete was refused: what would lose the skill. */
export interface SkillInUse {
  usages: SkillUsage[];
  /** Usages that belong to other members, which the signed-in user may not see. */
  hiddenCount: number;
}

/** The refusal behind `err` when it is a `skill_in_use` 409, else null. */
export function skillInUseOf(err: unknown): SkillInUse | null {
  const ax = err as AxiosError<{ error?: unknown; details?: Partial<SkillInUse> }> | undefined;
  const body = ax?.response?.data;
  if (ax?.response?.status !== 409 || body?.error !== 'skill_in_use') {
    return null;
  }
  return {
    usages: Array.isArray(body.details?.usages) ? body.details.usages : [],
    hiddenCount: typeof body.details?.hiddenCount === 'number' ? body.details.hiddenCount : 0,
  };
}

export async function updateAgentSkill(
  skillId: string,
  request: AgentSkillRequest,
  force = false,
): Promise<AgentSkill> {
  const response = await client.put<AgentSkill>(`/api/v1/agent-skills/${skillId}`, request, {
    params: force ? { force } : undefined,
  });
  return response.data;
}

export async function deleteAgentSkill(skillId: string, force = false): Promise<void> {
  await client.delete(`/api/v1/agent-skills/${skillId}`, { params: force ? { force } : undefined });
}

/** Switches a skill on or off for the signed-in user alone. */
export async function setAgentSkillEnabled(key: string, enabled: boolean): Promise<void> {
  await client.put(`/api/v1/agent-skills/${encodeURIComponent(key)}/enabled`, { enabled });
}

export interface SkillFile {
  filename: string;
  sizeBytes: number;
}

export interface SkillFilesListResponse {
  files: SkillFile[];
}

export interface SkillFileRequest {
  filename: string;
  contentBase64: string;
}

export async function listSkillFiles(skillId: string): Promise<SkillFilesListResponse> {
  const response = await client.get<SkillFilesListResponse>(
    `/api/v1/agent-skills/${skillId}/files`,
  );
  return response.data;
}

export async function addSkillFile(
  skillId: string,
  request: SkillFileRequest,
): Promise<SkillFile> {
  const response = await client.post<SkillFile>(
    `/api/v1/agent-skills/${skillId}/files`,
    request,
  );
  return response.data;
}

export const skillFileUrl = (skillId: string, filename: string): string =>
  `/api/v1/agent-skills/${skillId}/files/${encodeURIComponent(filename)}`;

export async function deleteSkillFile(skillId: string, filename: string): Promise<void> {
  await client.delete(skillFileUrl(skillId, filename));
}
